// SolveAbsoluteEqualityTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

/* === UTIL === */
function gcd(a, b) { while (b !== 0) [a, b] = [b, a % b]; return Math.abs(a); }
function formatFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;
  if (frac === 0) return `${sign}${intPart}`;
  for (let d = 1; d <= 10; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g, den = d / g;
      return intPart === 0 ? `${sign}\\dfrac{${num}}{${den}}` : `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}
const coeffX = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
const withSign = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);

/* === GENERATE === */
export function generateTask() {
  // a, c losujemy tak, by NIE zachodziło a+c=0 ani a-c=0 (unikamy dzielenia przez 0 / sprzeczności)
  let a, c;
  do {
    a = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 5) + 1); // ±1..±5
    c = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 5) + 1); // ±1..±5
  } while (a + c === 0 || a - c === 0);

  // stałe b, d: ±1..±9
  const b = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 9) + 1);
  const d = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 9) + 1);

  // rozwiązania dla dwóch równań: ax+b = cx+d  oraz  ax+b = -(cx+d)
  const denom1 = a - c;            // ≠ 0 (gwarantujemy w pętli)
  const denom2 = a + c;            // ≠ 0 (gwarantujemy w pętli)
  const x1 = (d - b) / denom1;
  const x2 = (-d - b) / denom2;

  const ax = coeffX(a);
  const cx = coeffX(c);
  const bStr = withSign(b);
  const dStr = withSign(d);
  const latex = `\\left|${ax} ${bStr}\\right| = \\left|${cx} ${dStr}\\right|`;

  return { type: "SolveAbsoluteEqualityTask", data: { a, b, c, d, x1, x2, latex } };
}

/* === TASK (polecenie + równanie w jednej linijce) === */
export function renderTask({ data }) {
  return <p>Rozwiąż równanie: <InlineMath math={data.latex} /></p>;
}

/* === INPUT === */
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math="x_1 =" /></span>
        <FractionInput
          value={userInput?.x1 || ""}
          onChange={val => onChange({ ...userInput, x1: val })}
          disabled={disabled}
          label="x_1"
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math="x_2 =" /></span>
        <FractionInput
          value={userInput?.x2 || ""}
          onChange={val => onChange({ ...userInput, x2: val })}
          disabled={disabled}
          label="x_2"
        />
      </div>
    </div>
  );
}

/* === CHECK === */
export function checkAnswer({ data }, userInput) {
  const { x1, x2 } = data;
  const u1 = userInput?.x1?.parsed;
  const u2 = userInput?.x2?.parsed;
  const correct =
    (Math.abs(u1 - x1) < 0.01 && Math.abs(u2 - x2) < 0.01) ||
    (Math.abs(u1 - x2) < 0.01 && Math.abs(u2 - x1) < 0.01);
  return { correct, expected: [x1, x2] };
}

/* === ANSWER (większy odstęp, do lewej) === */
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div style={{ marginBottom: "0.6rem" }}>
        <InlineMath math={`x_1 = ${formatFractionLatex(data.x1)}`} />
      </div>
      <div>
        <InlineMath math={`x_2 = ${formatFractionLatex(data.x2)}`} />
      </div>
    </div>
  );
}

/* === USER ANSWER (większy odstęp, do lewej) === */
export function renderUserAnswer(userInput) {
  const rawX1 = userInput?.x1?.raw || "";
  const rawX2 = userInput?.x2?.raw || "";
  if (!rawX1 && !rawX2) return <i>(brak odpowiedzi)</i>;
  const x1 = formatLatexPreview(rawX1, "x_1");
  const x2 = formatLatexPreview(rawX2, "x_2");
  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      {rawX1 && (
        <div style={{ marginBottom: "0.6rem" }}>
          <InlineMath math={x1} />
        </div>
      )}
      {rawX2 && <div><InlineMath math={x2} /></div>}
    </div>
  );
}

/* === EXPLANATION (wyśrodkowane rachunki) === */
export function renderExplanation({ data }) {
  const { a, b, c, d, x1, x2 } = data;

  const ax  = coeffX(a);
  const cx  = coeffX(c);
  const bS  = withSign(b);
  const dS  = withSign(d);

  // KROK 1 — rozpisanie bez strzałek, wyśrodkowane
  const k1a = `\\left|${ax} ${bS}\\right| = \\left|${cx} ${dS}\\right|`;
  const eq1 = `${ax} ${bS} = ${cx} ${dS}`;
  const eq2 = `${ax} ${bS} = -(${cx} ${dS})`;

  // KROK 2 — dwa równania: dodaj/odejmij → od razu wynik
  const line1_1 = eq1;
  const line1_2 = `${coeffX(a - c)} = ${d - b}`;
  const line1_3 = `x_1 = ${formatFractionLatex(x1)}`;

  const line2_1  = eq2;
  const line2_1b = `${ax} ${bS} = ${coeffX(-c)} ${-d >= 0 ? `+ ${-d}` : `- ${Math.abs(-d)}`}`;
  const line2_2  = `${coeffX(a + c)} = ${-d - b}`;
  const line2_3  = `x_2 = ${formatFractionLatex(x2)}`;

  return (
    <div className="space-y-4">
      <p><strong>Krok 1:</strong> Rozpisujemy wartość bezwzględną na dwa równania.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={k1a} />
        <BlockMath math={`${eq1}\\;\\;\\text{lub}\\;\\; ${eq2}`} />
      </div>

      <p><strong>Krok 2:</strong> Rozwiązujemy oba równania.</p>

      <p><em>Pierwsze równanie:</em></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={line1_1} />
        <BlockMath math={line1_2} />
        <BlockMath math={line1_3} />
      </div>

      <p><em>Drugie równanie:</em></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={line2_1} />
        <BlockMath math={line2_1b} />
        <BlockMath math={line2_2} />
        <BlockMath math={line2_3} />
      </div>

      <p><strong>Odpowiedź końcowa:</strong></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x_1 = ${formatFractionLatex(x1)},\\quad x_2 = ${formatFractionLatex(x2)}`} />
      </div>
    </div>
  );
}
